import { localParts, rangeStats } from "../glance";
import { PointIcon } from "../icon";
import { boundPoint, Frame, key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

/** Ranges whose low and high show the time they happened (WLB-12). */
const TIMED = new Set(["1h", "6h", "24h", "today"]);

/**
 * stats: the lowest, average and highest value over the range, when the low and the high were, and a range bar with
 * the current value marked (VisionPlatform widget-library-2 WLB-12).
 */
export function Stats({ widget, input, tz }: WidgetProps) {
  const fmt = useWidgetText();
  const bound = boundPoint(widget, input);
  const point = bound?.point;
  const name = widget.title ?? point?.name ?? "";
  const cfg = widget.config ?? {};
  const range = typeof cfg.range === "string" ? cfg.range : "24h";
  const history = bound?.binding.datapoint
    ? input.history?.[key(bound.binding.device, bound.binding.datapoint)]
    : undefined;
  const s = rangeStats(history);
  if (!point || !s) {
    return (
      <Frame widget={widget} state="empty" title={name} icon={<PointIcon point={point} />}>
        <p aria-label={`${name}: ${fmt.t("state.noData")}`} className="text-gray-700">
          {fmt.t("state.noData")}
        </p>
      </Frame>
    );
  }
  const decimals = typeof cfg.decimals === "number" ? cfg.decimals : point.decimals;
  const f = (v: number) => fmt.value(v, point, decimals);
  const at = (t: number) => (TIMED.has(range) ? fmt.t("stats.at", { time: localParts(t, tz).hhmm }) : undefined);
  const cells = [
    { k: "stats.min", v: f(s.min), at: at(s.minAt) },
    { k: "stats.avg", v: f(s.avg), at: undefined },
    { k: "stats.max", v: f(s.max), at: at(s.maxAt) },
  ];
  const label = `${name}: ${cells.map((c) => `${fmt.t(c.k)} ${c.v}`).join(", ")}`;
  const now = point.latest?.value;
  const pos = typeof now === "number" ? Math.min(1, Math.max(0, (now - s.min) / (s.max - s.min || 1))) : undefined;
  return (
    <Frame
      widget={widget}
      state={bound.device?.state === "offline" ? "offline" : "normal"}
      title={name}
      icon={<PointIcon point={point} />}
      label={label}
    >
      <dl className="grid grid-cols-3 gap-2 text-center">
        {cells.map((c) => (
          <div key={c.k} className="min-w-0">
            <dt className="text-xs text-gray-700">{fmt.t(c.k)}</dt>
            <dd className="truncate text-xl font-semibold tracking-tight text-gray-900 tabular-nums">{c.v}</dd>
            {c.at && <dd className="text-xs text-gray-700 tabular-nums">{c.at}</dd>}
          </div>
        ))}
      </dl>
      {pos !== undefined && typeof now === "number" && (
        // Right under the numbers, not at the bottom of a tall widget (owner's report #11).
        <div className="mt-2">
          <div aria-hidden="true" className="relative h-3">
            <div className="absolute inset-x-0 top-1 h-1.5 rounded-full bg-blue-200" />
            <div
              className="absolute top-0 h-3 w-1 -translate-x-1/2 rounded-sm bg-blue-700 ring-2 ring-white"
              style={{ left: `${(pos * 100).toFixed(1)}%` }}
            />
          </div>
          <p className="mt-0.5 flex justify-center gap-1 text-xs text-gray-700">
            <span>{fmt.t("stats.now")}</span>
            <span className="tabular-nums">{f(now)}</span>
          </p>
        </div>
      )}
    </Frame>
  );
}
